import { ReferralNetwork, Employee } from '../data/mockReferralData'

export type ReferralStats = {
  employee: Employee
  directCount: number
  indirectCount: number
  totalNetworkImpact: number
  directFundings: number // Sum of funding amounts from direct referrals (in thousands)
  indirectFundings: number // Sum of funding amounts from indirect referrals (in thousands)
}

/**
 * Get all descendants of a given employee (recursive DFS traversal)
 * Returns array of all employee IDs in the subtree
 */
function getAllDescendants(
  employeeId: string,
  referrals: Record<string, string[]>,
  visited: Set<string> = new Set(),
): string[] {
  // Prevent infinite loops in case of circular references
  if (visited.has(employeeId)) {
    return []
  }
  visited.add(employeeId)

  const directReferrals = referrals[employeeId] || []
  const allDescendants: string[] = [...directReferrals]

  for (const referredId of directReferrals) {
    const childDescendants = getAllDescendants(referredId, referrals, visited)
    allDescendants.push(...childDescendants)
  }

  return allDescendants
}

/**
 * Calculate indirect referrals for an employee
 * Indirect = all descendants beyond first level (excludes direct referrals)
 */
export function calculateIndirectReferrals(
  employeeId: string,
  referrals: Record<string, string[]>,
): number {
  const directReferrals = referrals[employeeId] || []
  let indirectCount = 0

  // For each direct referral, count all their descendants
  for (const directReferralId of directReferrals) {
    const descendants = getAllDescendants(
      directReferralId,
      referrals,
      new Set(),
    )
    indirectCount += descendants.length
  }

  return indirectCount
}

/**
 * Calculate direct fundings for an employee
 * Sum of funding amounts from direct referrals only
 */
export function calculateDirectFundings(
  employeeId: string,
  referrals: Record<string, string[]>,
  employees: Record<string, Employee>,
): number {
  const directReferrals = referrals[employeeId] || []
  return directReferrals.reduce((sum, referredId) => {
    const employee = employees[referredId]
    return sum + (employee?.fundingAmount || 0)
  }, 0)
}

/**
 * Calculate indirect fundings for an employee
 * Sum of funding amounts from all indirect referrals (descendants beyond first level)
 */
export function calculateIndirectFundings(
  employeeId: string,
  referrals: Record<string, string[]>,
  employees: Record<string, Employee>,
): number {
  const directReferrals = referrals[employeeId] || []
  let indirectFundings = 0

  // For each direct referral, sum all their descendants' funding amounts
  for (const directReferralId of directReferrals) {
    const descendants = getAllDescendants(
      directReferralId,
      referrals,
      new Set(),
    )
    indirectFundings += descendants.reduce((sum, descendantId) => {
      const employee = employees[descendantId]
      return sum + (employee?.fundingAmount || 0)
    }, 0)
  }

  return indirectFundings
}

/**
 * Calculate referral statistics for all employees
 */
export function calculateAllReferralStats(
  network: ReferralNetwork,
): ReferralStats[] {
  const stats: ReferralStats[] = []

  for (const employeeId of Object.keys(network.employees)) {
    const employee = network.employees[employeeId]
    const directReferrals = network.referrals[employeeId] || []
    const directCount = directReferrals.length
    const indirectCount = calculateIndirectReferrals(
      employeeId,
      network.referrals,
    )
    const directFundings = calculateDirectFundings(
      employeeId,
      network.referrals,
      network.employees,
    )
    const indirectFundings = calculateIndirectFundings(
      employeeId,
      network.referrals,
      network.employees,
    )

    stats.push({
      employee,
      directCount,
      indirectCount,
      totalNetworkImpact: directCount + indirectCount,
      directFundings,
      indirectFundings,
    })
  }

  // Sort by total network impact by default (descending)
  return stats.sort((a, b) => b.totalNetworkImpact - a.totalNetworkImpact)
}

/**
 * Build tree structure for visualization
 * Returns nodes and edges for @xyflow/react
 */
export type TreeNode = {
  id: string
  employee: Employee
  depth: number
  children: TreeNode[]
}

export function buildReferralTree(
  rootEmployeeId: string,
  network: ReferralNetwork,
  depth: number = 0,
  visited: Set<string> = new Set(),
): TreeNode | null {
  if (visited.has(rootEmployeeId)) {
    return null
  }
  visited.add(rootEmployeeId)

  const employee = network.employees[rootEmployeeId]
  if (!employee) {
    return null
  }

  const directReferrals = network.referrals[rootEmployeeId] || []
  const children: TreeNode[] = []

  for (const referredId of directReferrals) {
    const childNode = buildReferralTree(referredId, network, depth + 1, visited)
    if (childNode) {
      children.push(childNode)
    }
  }

  return {
    id: rootEmployeeId,
    employee,
    depth,
    children,
  }
}

/**
 * Convert tree structure to @xyflow/react nodes and edges
 */
export type FlowNode = {
  id: string
  type: string
  position: { x: number; y: number }
  data: {
    employee: Employee
    depth: number
    isRoot: boolean
  }
}

export type FlowEdge = {
  id: string
  source: string
  target: string
  type: string
  animated?: boolean
}

export function treeToFlowElements(
  tree: TreeNode,
  nodeWidth: number = 220,
  nodeHeight: number = 100,
  horizontalSpacing: number = 60,
  verticalSpacing: number = 120,
): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const nodes: FlowNode[] = []
  const edges: FlowEdge[] = []

  // Calculate positions using a simple recursive layout
  function calculateSubtreeWidth(node: TreeNode): number {
    if (node.children.length === 0) {
      return nodeWidth
    }
    const childrenWidth = node.children.reduce(
      (sum, child) => sum + calculateSubtreeWidth(child) + horizontalSpacing,
      -horizontalSpacing,
    )
    return Math.max(nodeWidth, childrenWidth)
  }

  function positionNodes(
    node: TreeNode,
    x: number,
    y: number,
    parentId: string | null,
  ): void {
    // Add node
    nodes.push({
      id: node.id,
      type: 'referralNode',
      position: { x, y },
      data: {
        employee: node.employee,
        depth: node.depth,
        isRoot: parentId === null,
      },
    })

    // Add edge from parent
    if (parentId) {
      edges.push({
        id: `edge-${parentId}-${node.id}`,
        source: parentId,
        target: node.id,
        type: 'smoothstep',
        animated: false,
      })
    }

    // Position children
    if (node.children.length > 0) {
      const totalWidth = node.children.reduce(
        (sum, child) => sum + calculateSubtreeWidth(child) + horizontalSpacing,
        -horizontalSpacing,
      )
      let currentX = x - totalWidth / 2 + nodeWidth / 2

      for (const child of node.children) {
        const childWidth = calculateSubtreeWidth(child)
        const childX = currentX + childWidth / 2 - nodeWidth / 2
        positionNodes(child, childX, y + nodeHeight + verticalSpacing, node.id)
        currentX += childWidth + horizontalSpacing
      }
    }
  }

  positionNodes(tree, 0, 0, null)

  return { nodes, edges }
}
